import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Plus, FileSpreadsheet, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { localToday } from '@/shared/utils/utils';

/**
 * These three screens (Payment/Bank/Employee Accounts) are still exactly
 * what the 2026-08-23 audit found: local useState seeded from the hardcoded
 * arrays below, no API calls, nothing persists on reload — unlike Bank
 * Reconciliation, which was ported to a real backend (see
 * BankReconciliationV2Page.tsx and src/api-accounting/banking.js) and IS
 * real. They carry no sidebar entry for exactly this reason. This banner is
 * the safety net for anyone who still lands here by direct URL, so nobody
 * mistakes what they add/edit/delete here for something that was saved.
 */
function NotYetBuiltBanner() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800/60 dark:bg-amber-950/30">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
      <div className="min-w-0 flex-1 text-sm text-amber-900 dark:text-amber-200">
        <p className="font-semibold">Nothing on this screen is saved.</p>
        <p className="mt-1 text-amber-800 dark:text-amber-300">
          This screen has no backend yet — every row shown is placeholder data, and anything you
          add, edit or delete here resets on your next page load. It has been kept out of the
          sidebar for this reason.
        </p>
      </div>
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface PaymentAccount {
  id: string;
  name: string;
  type: string;
  linkedBank: string;
  linkedEmployee: string;
  linkedLedger: string;
  vpa: string;
  createdAt: string;
  status: 'active' | 'inactive';
}

interface BankAccount {
  id: string;
  bankName: string;
  accNumber: string;
  sortCode: string;
  ifsc: string;
  iban: string;
  swift: string;
  holderName: string;
  accType: string;
  country: string;
  currency: string;
  status: 'active' | 'inactive';
}

interface EmployeeAccount {
  id: string;
  empId: string;
  name: string;
  dept: string;
  level: string;
  phone: string;
  country: string;
  currency: string;
  createdAt: string;
  status: 'active' | 'inactive';
}

// ── Initial Mock Data ──────────────────────────────────────────────────────────
const INITIAL_PAYMENTS: PaymentAccount[] = [
  { id: '1', name: 'Razorpay Primary QR', type: 'UPI Merchant', linkedBank: 'HDFC Bank (8821)', linkedEmployee: '—', linkedLedger: '1020 - HDFC Bank', vpa: 'zynx@hdfcbank', createdAt: '2026-08-01', status: 'active' },
  { id: '2', name: 'Paytm Petty Cash Vault', type: 'Digital Wallet', linkedBank: 'ICICI Bank (1042)', linkedEmployee: 'Rahul Sharma', linkedLedger: '1010 - Cash on Hand', vpa: 'paytm-zynx@paytm', createdAt: '2026-07-15', status: 'active' },
];

const INITIAL_BANKS: BankAccount[] = [
  { id: '1', bankName: 'HDFC Bank', accNumber: '50100239108821', sortCode: '—', ifsc: 'HDFC0000240', iban: '—', swift: 'HDFCINBBXXX', holderName: 'ZYNX ENTERPRISES PRIVATE LIMITED', accType: 'Current', country: 'India', currency: 'INR', status: 'active' },
  { id: '2', bankName: 'ICICI Corporate Bank', accNumber: '000405011042', sortCode: '—', ifsc: 'ICIC0000004', iban: '—', swift: 'ICICINBB', holderName: 'ZYNX ENTERPRISES PRIVATE LIMITED', accType: 'Current', country: 'India', currency: 'INR', status: 'active' },
];

const INITIAL_EMPLOYEES: EmployeeAccount[] = [
  { id: '1', empId: 'EMP-001', name: 'Rahul Sharma', dept: 'Sales & Marketing', level: 'L3 - Senior Manager', phone: '+91 98765 43210', country: 'India', currency: 'INR', createdAt: '2026-01-10', status: 'active' },
  { id: '2', empId: 'EMP-002', name: 'Priya Patel', dept: 'Finance & Accounts', level: 'L2 - Executive', phone: '+91 98123 45678', country: 'India', currency: 'INR', createdAt: '2026-03-01', status: 'active' },
];

// Helper to export CSV
function downloadCSVFile(filename: string, headers: string[], rows: (string | number)[][]) {
  const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success(`Exported ${filename}.csv`);
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Payment Accounts Page
// ─────────────────────────────────────────────────────────────────────────────
export function BankingPaymentAccountsPage() {
  const [payments, setPayments] = useState<PaymentAccount[]>(INITIAL_PAYMENTS);
  const [statusTab, setStatusTab] = useState<'active' | 'inactive'>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form State
  const [form, setForm] = useState({ name: '', type: 'UPI Merchant', linkedBank: '', linkedEmployee: '', linkedLedger: '', vpa: '' });

  const filtered = payments.filter(p => p.status === statusTab);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) return toast.error('Account Name is required');

    const newItem: PaymentAccount = {
      id: String(Date.now()),
      name: form.name,
      type: form.type,
      linkedBank: form.linkedBank || '—',
      linkedEmployee: form.linkedEmployee || '—',
      linkedLedger: form.linkedLedger || '1020 - Bank',
      vpa: form.vpa || '—',
      createdAt: localToday(),
      status: 'active',
    };

    setPayments(prev => [newItem, ...prev]);
    setIsModalOpen(false);
    setForm({ name: '', type: 'UPI Merchant', linkedBank: '', linkedEmployee: '', linkedLedger: '', vpa: '' });
    toast.success('Payment Account Created Successfully!');
  };

  const handleDelete = (id: string) => {
    setPayments(prev => prev.filter(p => p.id !== id));
    toast.success('Payment account removed');
  };

  const handleExportCSV = () => {
    downloadCSVFile(
      'Payment_Accounts',
      ['Payment Account', 'Account Type', 'Linked Bank', 'Linked Employee', 'Linked Ledger', 'VPA', 'Created At'],
      filtered.map(p => [p.name, p.type, p.linkedBank, p.linkedEmployee, p.linkedLedger, p.vpa, p.createdAt])
    );
  };

  return (
    <div className="p-6 space-y-6">
      <NotYetBuiltBanner />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Payment Accounts</h1>
        <Button onClick={() => setIsModalOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 h-9 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Payments Account
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 text-xs font-semibold border-b border-border/60 pb-1">
          <button onClick={() => setStatusTab('active')} className={`pb-2 border-b-2 transition ${statusTab === 'active' ? 'border-purple-600 text-purple-600' : 'border-transparent text-muted-foreground'}`}>
            Active Accounts ({payments.filter(p => p.status === 'active').length})
          </button>
          <button onClick={() => setStatusTab('inactive')} className={`pb-2 border-b-2 transition ${statusTab === 'inactive' ? 'border-purple-600 text-purple-600' : 'border-transparent text-muted-foreground'}`}>
            Inactive Accounts ({payments.filter(p => p.status === 'inactive').length})
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground font-medium">{filtered.length} Payment Accounts Found</span>
          <Button onClick={handleExportCSV} variant="outline" size="sm" className="h-8 text-xs font-medium flex items-center gap-1.5">
            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
            Download CSV
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-white dark:bg-slate-950 shadow-2xs">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-border text-slate-500 font-medium text-xs">
            <tr>
              <th className="px-5 py-3.5">Payment Account</th>
              <th className="px-5 py-3.5">Account Type</th>
              <th className="px-5 py-3.5">Linked Bank</th>
              <th className="px-5 py-3.5">Linked Employee</th>
              <th className="px-5 py-3.5">Linked Ledger</th>
              <th className="px-5 py-3.5">VPA</th>
              <th className="px-5 py-3.5">Created At</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-12 text-center text-xs text-muted-foreground font-medium">
                  No Payment Accounts Found
                </td>
              </tr>
            ) : (
              filtered.map(p => (
                <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors">
                  <td className="px-5 py-3.5 font-bold text-foreground">{p.name}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{p.type}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{p.linkedBank}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{p.linkedEmployee}</td>
                  <td className="px-5 py-3.5 text-xs font-mono text-purple-600">{p.linkedLedger}</td>
                  <td className="px-5 py-3.5 text-xs font-mono text-foreground">{p.vpa}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{p.createdAt}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button onClick={() => handleDelete(p.id)} className="p-1 text-rose-500 hover:bg-rose-50 rounded transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New Payment Account Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create New Payment Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold">Payment Account Name *</label>
              <Input placeholder="e.g. Razorpay POS / Cash Vault" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Account Type</label>
                <Select name="type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} options={[
                  { value: 'UPI Merchant', label: 'UPI Merchant' },
                  { value: 'Digital Wallet', label: 'Digital Wallet' },
                  { value: 'Payment Gateway', label: 'Payment Gateway' },
                  { value: 'POS Terminal', label: 'POS Terminal' },
                ]} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">VPA / UPI ID</label>
                <Input placeholder="merchant@upi" value={form.vpa} onChange={e => setForm({ ...form, vpa: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Linked Bank</label>
                <Input placeholder="HDFC Bank" value={form.linkedBank} onChange={e => setForm({ ...form, linkedBank: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Linked Employee</label>
                <Input placeholder="Employee Name" value={form.linkedEmployee} onChange={e => setForm({ ...form, linkedEmployee: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold">Linked Ledger Account</label>
              <Input placeholder="1020 - HDFC Bank" value={form.linkedLedger} onChange={e => setForm({ ...form, linkedLedger: e.target.value })} className="mt-1" />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700">Save Account</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Bank Accounts Page
// ─────────────────────────────────────────────────────────────────────────────
export function BankingBankAccountsPage() {
  const [banks, setBanks] = useState<BankAccount[]>(INITIAL_BANKS);
  const [statusTab, setStatusTab] = useState<'active' | 'inactive'>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [form, setForm] = useState({ bankName: '', accNumber: '', ifsc: '', holderName: '', accType: 'Current', country: 'India', currency: 'INR', swift: '' });

  const filtered = banks.filter(b => b.status === statusTab);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.bankName || !form.accNumber) return toast.error('Bank Name and Account Number required');

    const newItem: BankAccount = {
      id: String(Date.now()),
      bankName: form.bankName,
      accNumber: form.accNumber,
      sortCode: '—',
      ifsc: form.ifsc || '—',
      iban: '—',
      swift: form.swift || '—',
      holderName: form.holderName || 'ZYNX ENTERPRISES',
      accType: form.accType,
      country: form.country,
      currency: form.currency,
      status: 'active',
    };

    setBanks(prev => [newItem, ...prev]);
    setIsModalOpen(false);
    setForm({ bankName: '', accNumber: '', ifsc: '', holderName: '', accType: 'Current', country: 'India', currency: 'INR', swift: '' });
    toast.success('Bank Account Added Successfully!');
  };

  const handleDelete = (id: string) => {
    setBanks(prev => prev.filter(b => b.id !== id));
    toast.success('Bank account removed');
  };

  const handleExportCSV = () => {
    downloadCSVFile(
      'Bank_Accounts',
      ['Bank Name', 'Account Number', 'Sort Code', 'IFSC', 'IBAN', 'SWIFT', 'Account Holder Name', 'Account Type', 'Country', 'Currency'],
      filtered.map(b => [b.bankName, b.accNumber, b.sortCode, b.ifsc, b.iban, b.swift, b.holderName, b.accType, b.country, b.currency])
    );
  };

  return (
    <div className="p-6 space-y-6">
      <NotYetBuiltBanner />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Bank Accounts</h1>
        <Button onClick={() => setIsModalOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 h-9 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Bank Account
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 text-xs font-semibold border-b border-border/60 pb-1">
          <button onClick={() => setStatusTab('active')} className={`pb-2 border-b-2 transition ${statusTab === 'active' ? 'border-purple-600 text-purple-600' : 'border-transparent text-muted-foreground'}`}>
            Active Accounts ({banks.filter(b => b.status === 'active').length})
          </button>
          <button onClick={() => setStatusTab('inactive')} className={`pb-2 border-b-2 transition ${statusTab === 'inactive' ? 'border-purple-600 text-purple-600' : 'border-transparent text-muted-foreground'}`}>
            Inactive Accounts ({banks.filter(b => b.status === 'inactive').length})
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground font-medium">{filtered.length} Bank Account Found</span>
          <Button onClick={handleExportCSV} variant="outline" size="sm" className="h-8 text-xs font-medium flex items-center gap-1.5">
            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
            Download CSV
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-x-auto bg-white dark:bg-slate-950 shadow-2xs">
        <table className="w-full text-sm text-left border-collapse min-w-[950px]">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-border text-slate-500 font-medium text-xs">
            <tr>
              <th className="px-5 py-3.5">Bank Name</th>
              <th className="px-5 py-3.5">Account Number</th>
              <th className="px-5 py-3.5">Sort Code</th>
              <th className="px-5 py-3.5">IFSC</th>
              <th className="px-5 py-3.5">IBAN</th>
              <th className="px-5 py-3.5">SWIFT</th>
              <th className="px-5 py-3.5">Account Holder Name</th>
              <th className="px-5 py-3.5">Account Type</th>
              <th className="px-5 py-3.5">Country</th>
              <th className="px-5 py-3.5">Currency</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-5 py-12 text-center text-xs text-muted-foreground font-medium">
                  No Bank Accounts Found
                </td>
              </tr>
            ) : (
              filtered.map(b => (
                <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors">
                  <td className="px-5 py-3.5 font-bold text-foreground">{b.bankName}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-foreground">{b.accNumber}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{b.sortCode}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-purple-600">{b.ifsc}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{b.iban}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground">{b.swift}</td>
                  <td className="px-5 py-3.5 text-xs font-medium text-foreground">{b.holderName}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{b.accType}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{b.country}</td>
                  <td className="px-5 py-3.5 text-xs font-bold text-emerald-600">{b.currency}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button onClick={() => handleDelete(b.id)} className="p-1 text-rose-500 hover:bg-rose-50 rounded transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Bank Name *</label>
                <Input placeholder="HDFC Bank" value={form.bankName} onChange={e => setForm({ ...form, bankName: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Account Number *</label>
                <Input placeholder="50100XXXXXX" value={form.accNumber} onChange={e => setForm({ ...form, accNumber: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">IFSC Code</label>
                <Input placeholder="HDFC0000240" value={form.ifsc} onChange={e => setForm({ ...form, ifsc: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">SWIFT Code</label>
                <Input placeholder="HDFCINBBXXX" value={form.swift} onChange={e => setForm({ ...form, swift: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold">Account Holder Name</label>
              <Input placeholder="Company / Entity Name" value={form.holderName} onChange={e => setForm({ ...form, holderName: e.target.value })} className="mt-1" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold">Type</label>
                <Select name="accType" value={form.accType} onChange={e => setForm({ ...form, accType: e.target.value })} options={[
                  { value: 'Current', label: 'Current' },
                  { value: 'Savings', label: 'Savings' },
                  { value: 'Overdraft', label: 'Overdraft' },
                ]} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Country</label>
                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Currency</label>
                <Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="mt-1" />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700">Add Bank Account</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Employee Accounts Page
// ─────────────────────────────────────────────────────────────────────────────
export function BankingEmployeeAccountsPage() {
  const [employees, setEmployees] = useState<EmployeeAccount[]>(INITIAL_EMPLOYEES);
  const [statusTab, setStatusTab] = useState<'active' | 'inactive'>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [form, setForm] = useState({ name: '', dept: 'Sales', level: 'L2 - Executive', phone: '', country: 'India', currency: 'INR' });

  const filtered = employees.filter(e => e.status === statusTab);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) return toast.error('Employee Name required');

    const newItem: EmployeeAccount = {
      id: String(Date.now()),
      empId: `EMP-00${employees.length + 1}`,
      name: form.name,
      dept: form.dept,
      level: form.level,
      phone: form.phone || '+91 98000 00000',
      country: form.country,
      currency: form.currency,
      createdAt: localToday(),
      status: 'active',
    };

    setEmployees(prev => [newItem, ...prev]);
    setIsModalOpen(false);
    setForm({ name: '', dept: 'Sales', level: 'L2 - Executive', phone: '', country: 'India', currency: 'INR' });
    toast.success('Employee Account Added Successfully!');
  };

  const handleDelete = (id: string) => {
    setEmployees(prev => prev.filter(e => e.id !== id));
    toast.success('Employee account removed');
  };

  const handleExportCSV = () => {
    downloadCSVFile(
      'Employee_Accounts',
      ['Employee ID', 'Name', 'Department', 'Level', 'Phone', 'Country', 'Currency', 'Created At'],
      filtered.map(e => [e.empId, e.name, e.dept, e.level, e.phone, e.country, e.currency, e.createdAt])
    );
  };

  return (
    <div className="p-6 space-y-6">
      <NotYetBuiltBanner />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Employee Accounts</h1>
        <Button onClick={() => setIsModalOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 h-9 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Employee Account
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 text-xs font-semibold border-b border-border/60 pb-1">
          <button onClick={() => setStatusTab('active')} className={`pb-2 border-b-2 transition ${statusTab === 'active' ? 'border-purple-600 text-purple-600' : 'border-transparent text-muted-foreground'}`}>
            Active Accounts ({employees.filter(e => e.status === 'active').length})
          </button>
          <button onClick={() => setStatusTab('inactive')} className={`pb-2 border-b-2 transition ${statusTab === 'inactive' ? 'border-purple-600 text-purple-600' : 'border-transparent text-muted-foreground'}`}>
            Inactive Accounts ({employees.filter(e => e.status === 'inactive').length})
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground font-medium">{filtered.length} Employee Found</span>
          <Button onClick={handleExportCSV} variant="outline" size="sm" className="h-8 text-xs font-medium flex items-center gap-1.5">
            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
            Download CSV
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-white dark:bg-slate-950 shadow-2xs">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-border text-slate-500 font-medium text-xs">
            <tr>
              <th className="px-5 py-3.5">Employee ID</th>
              <th className="px-5 py-3.5">Name</th>
              <th className="px-5 py-3.5">Department</th>
              <th className="px-5 py-3.5">Level</th>
              <th className="px-5 py-3.5">Phone</th>
              <th className="px-5 py-3.5">Country</th>
              <th className="px-5 py-3.5">Currency</th>
              <th className="px-5 py-3.5">Created At</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-xs text-muted-foreground font-medium">
                  No Employee Accounts Found
                </td>
              </tr>
            ) : (
              filtered.map(e => (
                <tr key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors">
                  <td className="px-5 py-3.5 font-mono text-xs font-bold text-purple-600">{e.empId}</td>
                  <td className="px-5 py-3.5 font-bold text-foreground">{e.name}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{e.dept}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{e.level}</td>
                  <td className="px-5 py-3.5 text-xs font-mono text-foreground">{e.phone}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{e.country}</td>
                  <td className="px-5 py-3.5 text-xs font-bold text-emerald-600">{e.currency}</td>
                  <td className="px-5 py-3.5 text-xs text-muted-foreground">{e.createdAt}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button onClick={() => handleDelete(e.id)} className="p-1 text-rose-500 hover:bg-rose-50 rounded transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Employee Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold">Employee Name *</label>
              <Input placeholder="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Department</label>
                <Input placeholder="Sales / Finance" value={form.dept} onChange={e => setForm({ ...form, dept: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Level</label>
                <Input placeholder="L2 - Executive" value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold">Phone Number</label>
              <Input placeholder="+91 98765 43210" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Country</label>
                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Currency</label>
                <Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="mt-1" />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700">Add Employee</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
